/**
 * The AI-mode answer panel (#626).
 *
 * Shows the model's prose answer plus a per-pick justification. The justifications are the
 * point, not decoration: measured on the golden set, retrieval scores 0% P@1 on every
 * `multi-constraint` case, so the model is doing work the user cannot verify by reading the
 * result list. When it excludes a candidate for violating a stated constraint ("not for
 * rally"), the only way to know it understood rather than ignored the constraint is for it to
 * say so. A silent reorder would be unauditable.
 *
 * NAMES, NOT INSTALL NAMES. The model works in `installName` because that is the catalog's
 * unique id and the allowlist key — but an installName is machine-facing and often awful to
 * read (`pr--skills--pr-review`, `dev-skills-v1--skills--ccs-unit-test-generator`). The panel
 * therefore renders `pluginName` with `org/repo` beneath it, exactly as PluginCard does, so a
 * recommendation and the card it refers to are recognisably the same thing.
 *
 * WHAT IS DELIBERATELY NOT SHOWN: the pre-model retrieval rank. It was rendered as "was #18" on
 * the theory that exposing the reranking makes a bad answer diagnosable — but that answers a
 * question no user asked, and reads as debug output. The provenance still exists where it is
 * actually used: the server logs it per query, and the eval harness grades against it.
 */

import { useEffect, useState } from "react";
import type { JudgeResult, AiFailureReason } from "../lib/ai-mode";
import type { MarketplaceEntry } from "../types/index";
import styles from "./AiAnswer.module.css";
import SparkleIcon from "../icons/SparkleIcon";
import CloseButtonIcon from "../icons/CloseIcon";

/**
 * The loading state, extracted so it can own a ticking elapsed-time counter without adding a
 * hook to the main component (whose early returns would violate the rules-of-hooks).
 *
 * WHY a live timer + Cancel: the judge runs up to the ~24s deadline, and a static "a few
 * seconds" for that long reads as a hang. A ticking count reassures the user something is
 * happening, and Cancel is the escape hatch — it reuses onDismiss, which drops `aiRequested`
 * and lets the fetch effect's cleanup abort the in-flight request, so the user is not trapped
 * watching a slow call they no longer want.
 */
function LoadingPanel({ onCancel }: { onCancel: () => void }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.spinner} aria-hidden="true" />
        Reading the catalog…
      </div>
      {/*
        The static sentence is the live-region content — announced once when loading starts.
        The ticking counter is aria-hidden so it is NOT in the accessibility tree: without this
        it re-announced the whole sentence to screen readers every second for the ~24s wait. It
        stays a purely visual progress cue.
      */}
      <p className={styles.answer} aria-live="polite">
        Comparing candidates against your request.{" "}
        <span className={styles.elapsed} aria-hidden="true">
          {elapsed}s
        </span>
      </p>
      <button type="button" className={styles.retry} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/**
 * One friendly line per failure reason — the ONLY thing the panel shows for a failure. Raw error
 * text (Bedrock messages, "HTTP 504") stays in the console/logs; the user reads plain language and
 * gets their regular results below regardless.
 */
const REASON_COPY: Record<AiFailureReason, string> = {
  truncated: "This question matches a lot of skills, so the AI couldn't finish. Try narrowing it.",
  timeout: "The AI took too long to respond.",
  busy: "AI mode is busy — try again in a minute.",
  session_expired: "Your session expired.",
  generic: "Something went wrong on our side.",
};

/**
 * The one calm "AI answer unavailable" panel, shown for EVERY failure kind (truncation, timeout,
 * busy, expired session, generic). Deliberately not the red alert — the regular results render
 * below, so nothing is broken for the user. `session_expired` swaps Try-again for a Sign-in
 * button; a correlation id, when present, becomes a copyable support reference.
 */
function FailureNotice({
  reason,
  correlationId,
  onRetry,
  onSignIn,
  onDismiss,
}: {
  reason: AiFailureReason;
  correlationId?: string;
  onRetry?: () => void;
  onSignIn?: () => void;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copyReference = () => {
    if (!correlationId) return;
    void navigator.clipboard?.writeText(correlationId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`${styles.panel} ${styles.notice}`} role="status">
      <div className={styles.header}>
        AI answer unavailable
        <button type="button" className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss">
          <CloseButtonIcon />
        </button>
      </div>
      <p className={styles.noticeText}>{REASON_COPY[reason]}</p>
      <p className={styles.answer}>Showing regular search results below.</p>

      {reason === "session_expired" && onSignIn ? (
        <button type="button" className={styles.retry} onClick={onSignIn}>
          Sign in again
        </button>
      ) : (
        onRetry && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            Try again
          </button>
        )
      )}

      {correlationId && (
        <p className={styles.reference}>
          Still not working? Ask in #ai-mcp with reference{" "}
          <span className={styles.refCode}>{correlationId}</span>
          <button type="button" className={styles.copyRef} onClick={copyReference}>
            {copied ? "Copied" : "Copy"}
          </button>
        </p>
      )}
    </div>
  );
}

/** A pick resolved to its catalog entry, so the panel can show names and navigate. */
export interface ResolvedPick {
  entry: MarketplaceEntry;
  /** The model's justification for this entry against this query. */
  why: string;
}

interface AiAnswerProps {
  judgement: JudgeResult | null;
  picks: ResolvedPick[];
  isLoading: boolean;
  /**
   * The single failure signal. Non-null → the one calm "AI answer unavailable" panel, with the
   * friendly line for that reason and the regular results below. It unifies what used to be two
   * separate branches (a red `error` alert and a calm `aiUnavailable` notice): there is no red
   * alert any more, and every failure kind — server degrade OR a failed request — routes here.
   */
  failureReason: AiFailureReason | null;
  /** Correlation id of the attempt, shown as a copyable support reference in the failure panel. */
  correlationId?: string;
  /** Opens the plugin detail view — same destination as clicking its card. */
  onSelect: (plugin: MarketplaceEntry) => void;
  /** Closes the panel and returns to plain results without re-running the search. */
  onDismiss: () => void;
  /** Re-run the request ("Try again"). Hidden for `session_expired`, which shows Sign in instead. */
  onRetry?: () => void;
  /** Begin a fresh sign-in — rendered as "Sign in again" only for the `session_expired` reason. */
  onSignIn?: () => void;
}

export default function AiAnswer({
  judgement,
  picks,
  isLoading,
  failureReason,
  correlationId,
  onSelect,
  onDismiss,
  onRetry,
  onSignIn,
}: AiAnswerProps) {
  // The judge takes up to ~24s, so show progress (a ticking counter) and an escape hatch
  // (Cancel) rather than a bare spinner. See LoadingPanel.
  if (isLoading) return <LoadingPanel onCancel={onDismiss} />;

  // Every failure — a server degrade (truncated/timeout/generic) OR a failed request
  // (busy/session_expired/generic) — is the one calm panel. Checked before `!judgement` because a
  // server degrade returns a null judgement, and this must win over the silent no-panel branch.
  if (failureReason) {
    return (
      <FailureNotice
        reason={failureReason}
        correlationId={correlationId}
        onRetry={onRetry}
        onSignIn={onSignIn}
        onDismiss={onDismiss}
      />
    );
  }

  if (!judgement) return null;

  return (
    <div className={`${styles.panel} ${judgement.nothingMatches ? styles.nothing : ""}`}>
      <div className={styles.header}>
        <SparkleIcon />
        {/*
          Verb/noun split is INTENTIONAL, not an inconsistency to "fix": the catalog control is
          a VERB ("Ask AI") because it is an action you take on the current query; this heading
          is a NOUN ("AI mode") because it labels the resulting panel — the state you are now in.
          Renaming either to match the other loses that action-vs-state distinction.
        */}
        {judgement.nothingMatches ? "No match found" : "AI mode"}
        <button type="button" className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss AI answer">
          <CloseButtonIcon />
        </button>
      </div>

      {/*
        Picks FIRST, prose after. The picks are the actionable payload — the skills the user
        can open — so they lead; the answer prose is framing/context and reads better as a
        follow-on than as a preamble the user must scroll past to reach the recommendations.
        On a "no match" there are no picks, so this collapses to just the prose, which is
        correct (the explanation IS the answer there).
      */}
      {picks.length > 0 && (
        <ul className={styles.reasons}>
          {picks.map(({ entry, why }) => (
            <li key={entry.installName ?? entry.displayName} className={styles.reason}>
              {/*
                Native <button>, not a styled div: keyboard handling, focus, and role come for
                free (WAI-ARIA APG), and PluginCard already uses a button for the same action.
              */}
              <button
                type="button"
                className={styles.pickName}
                onClick={() => onSelect(entry)}
              >
                {entry.pluginName}
              </button>
              <span className={styles.pickRepo}>
                {entry.orgOrOwner}/{entry.repoName}
              </span>
              <span className={styles.why}>{why}</span>
            </li>
          ))}
        </ul>
      )}

      <p className={`${styles.answer} ${picks.length > 0 ? styles.answerAfterPicks : ""}`} aria-live="polite">
        {judgement.answer}
      </p>

      {/*
        AI-generated content notice. Footer, muted, and shown only on a real answer (the
        loading and error states already say what they are). Frames the prose + picks above
        as model output the user should sanity-check — the standard trust affordance for a
        launched AI feature, and cheap insurance against a confidently-wrong recommendation
        being read as authoritative.
      */}
      <p className={styles.disclaimer}>
        Note: AI-generated responses can be incorrect. Double-check before relying on them.
      </p>
    </div>
  );
}
